// src/components/forms/GenericContactForm.jsx
import React, { useState, useContext } from "react";
import {
  Box,
  Input,
  Button,
  VStack,
  Heading,
  Text,
  Icon,
} from "@chakra-ui/react";
import { CheckCircle } from "lucide-react";
import ConsentNotice from "./ConsentNotice";
import prospectContext from "../../../context/prospect/prospectContext";

export default function GenericContactForm() {
  const { createProspect } = useContext(prospectContext);
  const [prospect, setProspect] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProspect((p) => ({
      ...p,
      [name]: value,
    }));
  };
  const reportConversion = (url) => {
    const callback = () => {
      if (typeof url !== "undefined") {
        window.location = url;
      }
    };

    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "conversion", {
        send_to: "AW-11436689616/odIVCPSrwp0bENCRuM0q",
        event_callback: callback,
      });
    }
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      fullName: prospect.fullName,
      email: prospect.email,
      phone: prospect.phone,
    };

    try {
      await createProspect(payload);
      setSubmitted(true);

      // 🔥 trigger Google Ads conversion event
      reportConversion(); // optional: add redirect URL inside ()
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      bg="white"
      p={{ base: 6, md: 8 }}
      borderRadius="xl"
      boxShadow="2xl"
      maxW={{ base: "100%", md: "450px" }}
      w="100%"
    >
      {submitted ? (
        // Success message
        <VStack spacing={6} py={8} textAlign="center">
          <Icon
            as={CheckCircle}
            boxSize={16}
            color="green.500"
            strokeWidth={2}
          />

          <VStack spacing={2}>
            <Heading size="lg" color="gray.800" fontWeight="700">
              Thank You!
            </Heading>

            <Text fontSize="md" color="gray.600" maxW="300px">
              Your information has been received. A tax relief expert will reach
              out to you shortly.
            </Text>
          </VStack>

          <Box
            w="100%"
            p={4}
            bg="blue.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderColor="blue.500"
          >
            <Text fontSize="sm" color="blue.800" fontWeight="600">
              📞 Expect a call within 24 hours
            </Text>
          </Box>
        </VStack>
      ) : (
        // Form
        <VStack spacing={4} align="stretch">
          <Heading
            size="md"
            color="gray.800"
            textAlign="center"
            fontWeight="700"
          >
            Get Matched with a Tax Relief Expert
          </Heading>

          <Text fontSize="sm" color="gray.600" textAlign="center">
            Free consultation • No obligation
          </Text>

          <form onSubmit={onSubmit}>
            <VStack spacing={3} align="stretch">
              <Input
                name="fullName"
                placeholder="Full Name"
                value={prospect.fullName}
                onChange={handleChange}
                required
                size="lg"
                bg="gray.50"
                color="gray.800" // ✅ Add this
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }} // ✅ Add this
                _hover={{ borderColor: "blue.400" }}
                _focus={{
                  borderColor: "blue.500",
                  bg: "white",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                }}
              />

              <Input
                name="email"
                type="email"
                placeholder="Email Address"
                value={prospect.email}
                onChange={handleChange}
                required
                size="lg"
                bg="gray.50"
                color="gray.800" // ✅ Add this
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }} // ✅ Add this
                _hover={{ borderColor: "blue.400" }}
                _focus={{
                  borderColor: "blue.500",
                  bg: "white",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                }}
              />

              <Input
                name="phone"
                type="tel"
                placeholder="Phone Number"
                value={prospect.phone}
                onChange={handleChange}
                required
                size="lg"
                bg="gray.50"
                color="gray.800" // ✅ Add this
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }} // ✅ Add this
                _hover={{ borderColor: "blue.400" }}
                _focus={{
                  borderColor: "blue.500",
                  bg: "white",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                }}
              />

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                w="100%"
                isLoading={submitting}
                loadingText="Submitting..."
                fontWeight="700"
                fontSize="md"
                py={6}
                bgGradient="linear(to-r, blue.500, blue.600)"
                _hover={{
                  bgGradient: "linear(to-r, blue.600, blue.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                transition="all 0.2s"
              >
                Get Matched
              </Button>
            </VStack>
          </form>

          <ConsentNotice />
        </VStack>
      )}
    </Box>
  );
}
